import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  PlayCircle, 
  PauseCircle, 
  RefreshCw, 
  Download,
  TrendingUp,
  Activity,
  BarChart,
  Info
} from 'lucide-react';
import { useApp } from '../../contexts/AppContext';
import { formatCurrency, formatPercent } from '../../utils/formatters';
import { Bar, Scatter } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

interface SimulationResult {
  scenario: number;
  npv: number;
  irr: number;
  cashOnCash: number;
  totalReturn: number;
  breakEvenYear: number;
  maxDrawdown: number;
  parameters: {
    rentGrowth: number;
    vacancy: number;
    interestRate: number;
    inflation: number;
    propertyAppreciation: number;
    maintenanceCost: number;
  };
}

interface SimulationStats {
  mean: number;
  median: number;
  stdDev: number;
  min: number;
  max: number;
  percentile5: number;
  percentile25: number;
  percentile75: number;
  percentile95: number;
  successRate: number;
  valueAtRisk: number;
}

const MonteCarloSimulation: React.FC = () => {
  const { currentInputs, portfolio } = useApp();
  const [isRunning, setIsRunning] = useState(false);
  const [progress, setProgress] = useState(0);
  const [results, setResults] = useState<SimulationResult[]>([]);
  const [stats, setStats] = useState<Record<string, SimulationStats> | null>(null);
  const [selectedMetric, setSelectedMetric] = useState<'npv' | 'irr' | 'cashOnCash' | 'totalReturn'>('npv');
  const [simulationCount, setSimulationCount] = useState(10000);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState<number>(-1); // -1 means use all portfolio
  const workerRef = useRef<Worker | null>(null);

  // Simulation parameters with ranges
  const [parameters, setParameters] = useState({
    rentGrowth: { min: -2, max: 5, current: 2.5 },
    vacancy: { min: 0, max: 20, current: 5 },
    interestRate: { min: 2, max: 7, current: 3.5 },
    inflation: { min: 0, max: 5, current: 2 },
    propertyAppreciation: { min: -2, max: 8, current: 3 },
    maintenanceCost: { min: 0.5, max: 3, current: 1.5 }
  });

  // Initialize Web Worker
  useEffect(() => {
    workerRef.current = new Worker('/monteCarloWorker.js');
    
    workerRef.current.onmessage = (e) => {
      const { type, data } = e.data;
      
      switch (type) {
        case 'progress':
          setProgress(data);
          break;
        case 'result':
          setResults(data.results);
          setStats(data.stats);
          setIsRunning(false);
          setProgress(100);
          break;
        case 'error':
          console.error('Simulation error:', data);
          setIsRunning(false);
          break;
      }
    };

    return () => {
      workerRef.current?.terminate();
    };
  }, []);

  const runSimulation = useCallback(() => {
    if (!workerRef.current) return;

    // Determine which properties to simulate
    let propertiesToSimulate: any[] = [];
    
    if (selectedProperty === -1) {
      // Use all portfolio properties
      if (portfolio.length > 0) {
        propertiesToSimulate = portfolio.map(p => p.inputs);
      } else if (currentInputs) {
        // Fall back to current inputs if no portfolio
        propertiesToSimulate = [currentInputs];
      }
    } else if (selectedProperty >= 0 && portfolio[selectedProperty]) {
      // Use specific property from portfolio
      propertiesToSimulate = [portfolio[selectedProperty].inputs];
    } else if (currentInputs) {
      // Fall back to current inputs
      propertiesToSimulate = [currentInputs];
    }

    if (propertiesToSimulate.length === 0) return;

    setIsRunning(true);
    setProgress(0);
    setResults([]);
    setStats(null);

    workerRef.current.postMessage({
      type: 'start',
      data: {
        properties: propertiesToSimulate,
        parameters,
        count: simulationCount
      }
    });
  }, [currentInputs, portfolio, selectedProperty, parameters, simulationCount]);

  const stopSimulation = useCallback(() => {
    if (!workerRef.current) return;
    
    workerRef.current.postMessage({ type: 'stop' });
    setIsRunning(false);
  }, []);

  const exportResults = useCallback(() => {
    if (!results.length) return;

    const csv = [
      ['Scenario', 'NPV', 'IRR', 'Cash-on-Cash', 'Total Return', 'Break Even Year', 'Max Drawdown', 
       'Rent Growth', 'Vacancy', 'Interest Rate', 'Inflation', 'Appreciation', 'Maintenance'],
      ...results.map(r => [
        r.scenario,
        r.npv,
        r.irr,
        r.cashOnCash,
        r.totalReturn,
        r.breakEvenYear,
        r.maxDrawdown,
        r.parameters.rentGrowth,
        r.parameters.vacancy,
        r.parameters.interestRate,
        r.parameters.inflation,
        r.parameters.propertyAppreciation,
        r.parameters.maintenanceCost
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `monte-carlo-simulation-${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [results]);

  const getDistributionData = () => {
    if (!results.length) return null;

    const values = results.map(r => {
      switch (selectedMetric) {
        case 'npv': return r.npv;
        case 'irr': return r.irr;
        case 'cashOnCash': return r.cashOnCash;
        case 'totalReturn': return r.totalReturn;
        default: return r.npv;
      }
    });

    // Create histogram bins
    const min = Math.min(...values);
    const max = Math.max(...values);
    const binCount = 50;
    const binSize = (max - min) / binCount;
    const bins = Array(binCount).fill(0);
    const binLabels = [];

    values.forEach(v => {
      const binIndex = Math.min(Math.floor((v - min) / binSize), binCount - 1);
      bins[binIndex]++;
    });

    for (let i = 0; i < binCount; i++) {
      const binStart = min + i * binSize;
      binLabels.push(binStart.toFixed(0));
    }

    return {
      labels: binLabels,
      datasets: [{
        label: 'Häufigkeit',
        data: bins,
        backgroundColor: 'rgba(99, 102, 241, 0.5)',
        borderColor: 'rgb(99, 102, 241)',
        borderWidth: 1
      }]
    };
  };

  const getScatterData = () => {
    if (!results.length) return null;

    return {
      datasets: [{
        label: 'NPV vs IRR',
        data: results.slice(0, 1000).map(r => ({ x: r.irr * 100, y: r.npv })),
        backgroundColor: 'rgba(99, 102, 241, 0.3)',
        borderColor: 'rgb(99, 102, 241)',
        pointRadius: 2
      }]
    };
  };

  const renderStatCard = (title: string, stat: SimulationStats | undefined, metric: string) => {
    if (!stat) return null;

    const isPercent = metric === 'irr' || metric === 'cashOnCash' || metric === 'totalReturn';
    const format = isPercent ? formatPercent : formatCurrency;

    return (
      <div className="bg-white rounded-lg p-4 border border-gray-200">
        <h3 className="font-semibold text-gray-700 mb-3">{title}</h3>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-600">Mittelwert:</span>
            <span className="font-medium">{format(isPercent ? stat.mean : stat.mean)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Median:</span>
            <span className="font-medium">{format(isPercent ? stat.median : stat.median)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Std. Abweichung:</span>
            <span className="font-medium">{format(isPercent ? stat.stdDev : stat.stdDev)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Min / Max:</span>
            <span className="font-medium">
              {format(isPercent ? stat.min : stat.min)} / {format(isPercent ? stat.max : stat.max)}
            </span>
          </div>
          <div className="border-t pt-2 mt-2">
            <div className="flex justify-between">
              <span className="text-gray-600">5% Perzentil:</span>
              <span className="font-medium text-red-600">{format(isPercent ? stat.percentile5 : stat.percentile5)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">95% Perzentil:</span>
              <span className="font-medium text-green-600">{format(isPercent ? stat.percentile95 : stat.percentile95)}</span>
            </div>
          </div>
          {metric === 'npv' && (
            <div className="border-t pt-2 mt-2">
              <div className="flex justify-between">
                <span className="text-gray-600">Erfolgsrate:</span>
                <span className="font-medium">{formatPercent(stat.successRate)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Value at Risk (5%):</span>
                <span className="font-medium text-orange-600">{formatCurrency(stat.valueAtRisk)}</span>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header Controls */}
      <div className="bg-white rounded-lg p-6 border border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900 flex items-center">
            <Activity className="w-6 h-6 mr-2 text-indigo-600" />
            Monte Carlo Simulation
          </h2>
          <div className="flex items-center space-x-4">
            {portfolio.length > 0 && (
              <div className="flex items-center">
                <label className="text-sm text-gray-600 mr-2">Immobilie:</label>
                <select
                  value={selectedProperty}
                  onChange={(e) => setSelectedProperty(Number(e.target.value))}
                  disabled={isRunning}
                  className="px-3 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value={-1}>Gesamtes Portfolio ({portfolio.length})</option>
                  {portfolio.map((property, idx) => (
                    <option key={idx} value={idx}>
                      {property.inputs.propertyName || `Immobilie ${idx + 1}`}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div className="flex items-center">
              <label className="text-sm text-gray-600 mr-2">Szenarien:</label>
              <select
                value={simulationCount}
                onChange={(e) => setSimulationCount(Number(e.target.value))}
                disabled={isRunning}
                className="px-3 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value={1000}>1.000</option>
                <option value={5000}>5.000</option>
                <option value={10000}>10.000</option>
                <option value={25000}>25.000</option>
                <option value={50000}>50.000</option>
              </select>
            </div>
            <button
              onClick={showAdvanced ? () => setShowAdvanced(false) : () => setShowAdvanced(true)}
              className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50"
            >
              {showAdvanced ? 'Einfach' : 'Erweitert'}
            </button>
          </div>
        </div>

        {/* Parameter Controls */}
        {showAdvanced && (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
            {Object.entries(parameters).map(([key, param]) => (
              <div key={key} className="space-y-1">
                <label className="text-sm font-medium text-gray-700">
                  {key === 'rentGrowth' && 'Mietsteigerung (%)'}
                  {key === 'vacancy' && 'Leerstand (%)'}
                  {key === 'interestRate' && 'Zinssatz (%)'}
                  {key === 'inflation' && 'Inflation (%)'}
                  {key === 'propertyAppreciation' && 'Wertsteigerung (%)'}
                  {key === 'maintenanceCost' && 'Instandhaltung (%)'}
                </label>
                <div className="flex items-center space-x-2 text-sm">
                  <input
                    type="number"
                    value={param.min}
                    onChange={(e) => setParameters(prev => ({
                      ...prev,
                      [key]: { ...prev[key as keyof typeof prev], min: Number(e.target.value) }
                    }))}
                    disabled={isRunning}
                    className="w-16 px-2 py-1 border border-gray-300 rounded"
                    step="0.5"
                  />
                  <span className="text-gray-500">bis</span>
                  <input
                    type="number"
                    value={param.max}
                    onChange={(e) => setParameters(prev => ({
                      ...prev,
                      [key]: { ...prev[key as keyof typeof prev], max: Number(e.target.value) }
                    }))}
                    disabled={isRunning}
                    className="w-16 px-2 py-1 border border-gray-300 rounded"
                    step="0.5"
                  />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex items-center justify-between">
          <div className="flex space-x-3">
            {!isRunning ? (
              <button
                onClick={runSimulation}
                disabled={!currentInputs && portfolio.length === 0}
                className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                <PlayCircle className="w-5 h-5 mr-2" />
                Simulation starten
              </button>
            ) : (
              <button
                onClick={stopSimulation}
                className="flex items-center px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
              >
                <PauseCircle className="w-5 h-5 mr-2" />
                Stoppen
              </button>
            )}
            {results.length > 0 && (
              <>
                <button
                  onClick={runSimulation}
                  disabled={isRunning}
                  className="flex items-center px-4 py-2 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
                >
                  <RefreshCw className="w-5 h-5 mr-2" />
                  Neu berechnen
                </button>
                <button
                  onClick={exportResults}
                  className="flex items-center px-4 py-2 border border-gray-300 rounded hover:bg-gray-50"
                >
                  <Download className="w-5 h-5 mr-2" />
                  CSV Export
                </button>
              </>
            )}
          </div>
          {isRunning && (
            <div className="flex items-center">
              <div className="w-48 bg-gray-200 rounded-full h-2 mr-3">
                <div 
                  className="bg-indigo-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <span className="text-sm text-gray-600">{progress}%</span>
            </div>
          )}
        </div>

        {!currentInputs && portfolio.length === 0 && (
          <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded">
            <div className="flex items-center text-yellow-800">
              <Info className="w-5 h-5 mr-2" />
              <span className="text-sm">
                Bitte fügen Sie Immobilien zum Portfolio hinzu oder geben Sie Daten im Analyzer ein, um die Simulation zu starten.
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Results */}
      {stats && (
        <>
          {/* Metric Selector */}
          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <div className="flex space-x-2">
              {(['npv', 'irr', 'cashOnCash', 'totalReturn'] as const).map(metric => (
                <button
                  key={metric}
                  onClick={() => setSelectedMetric(metric)}
                  className={`px-4 py-2 rounded ${
                    selectedMetric === metric
                      ? 'bg-indigo-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {metric === 'npv' && 'Kapitalwert (NPV)'}
                  {metric === 'irr' && 'Interne Rendite (IRR)'}
                  {metric === 'cashOnCash' && 'Cash-on-Cash'}
                  {metric === 'totalReturn' && 'Gesamtrendite'}
                </button>
              ))}
            </div>
          </div>

          {/* Statistics Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {renderStatCard('Kapitalwert (NPV)', stats?.npv, 'npv')}
            {renderStatCard('Interne Rendite (IRR)', stats?.irr, 'irr')}
            {renderStatCard('Cash-on-Cash Return', stats?.cashOnCash, 'cashOnCash')}
            {renderStatCard('Gesamtrendite', stats?.totalReturn, 'totalReturn')}
          </div>

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Distribution Chart */}
            <div className="bg-white rounded-lg p-6 border border-gray-200">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center">
                <BarChart className="w-5 h-5 mr-2 text-indigo-600" />
                Verteilung {selectedMetric === 'npv' ? 'NPV' : selectedMetric === 'irr' ? 'IRR' : selectedMetric === 'cashOnCash' ? 'Cash-on-Cash' : 'Gesamtrendite'}
              </h3>
              {getDistributionData() && (
                <Bar
                  data={getDistributionData()!}
                  options={{
                    responsive: true,
                    plugins: {
                      legend: { display: false },
                      tooltip: {
                        callbacks: {
                          label: (context) => `Anzahl: ${context.raw}`
                        }
                      }
                    },
                    scales: {
                      y: {
                        beginAtZero: true,
                        title: {
                          display: true,
                          text: 'Häufigkeit'
                        }
                      },
                      x: {
                        title: {
                          display: true,
                          text: selectedMetric === 'npv' ? 'NPV (€)' : '%'
                        }
                      }
                    }
                  }}
                />
              )}
            </div>

            {/* Scatter Plot */}
            <div className="bg-white rounded-lg p-6 border border-gray-200">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center">
                <TrendingUp className="w-5 h-5 mr-2 text-indigo-600" />
                NPV vs IRR Korrelation
              </h3>
              {getScatterData() && (
                <Scatter
                  data={getScatterData()!}
                  options={{
                    responsive: true,
                    plugins: {
                      legend: { display: false },
                      tooltip: {
                        callbacks: {
                          label: (context) => [
                            `IRR: ${context.parsed.x.toFixed(2)}%`,
                            `NPV: ${formatCurrency(context.parsed.y)}`
                          ]
                        }
                      }
                    },
                    scales: {
                      x: {
                        title: {
                          display: true,
                          text: 'IRR (%)'
                        }
                      },
                      y: {
                        title: {
                          display: true,
                          text: 'NPV (€)'
                        }
                      }
                    }
                  }}
                />
              )}
            </div>
          </div>

          {/* Risk Analysis Summary */}
          <div className="bg-white rounded-lg p-6 border border-gray-200">
            <h3 className="font-semibold text-gray-900 mb-4">Risikoanalyse Zusammenfassung</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Wahrscheinlichkeit positiver NPV:</span>
                  <span className={`font-semibold ${stats.npv.successRate > 0.7 ? 'text-green-600' : stats.npv.successRate > 0.5 ? 'text-yellow-600' : 'text-red-600'}`}>
                    {formatPercent(stats.npv.successRate)}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Durchschn. Break-Even Jahr:</span>
                  <span className="font-semibold">
                    {results.reduce((sum, r) => sum + r.breakEvenYear, 0) / results.length | 0} Jahre
                  </span>
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Max. Drawdown (Durchschn.):</span>
                  <span className="font-semibold text-red-600">
                    {formatCurrency(results.reduce((sum, r) => sum + r.maxDrawdown, 0) / results.length)}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Konfidenzintervall (90%):</span>
                  <span className="font-semibold">
                    {formatCurrency(stats.npv.percentile5)} - {formatCurrency(stats.npv.percentile95)}
                  </span>
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Sharpe Ratio:</span>
                  <span className="font-semibold">
                    {((stats.npv.mean - 0) / stats.npv.stdDev).toFixed(2)}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Sortino Ratio:</span>
                  <span className="font-semibold">
                    {((stats.npv.mean - 0) / Math.sqrt(results.filter(r => r.npv < 0).reduce((sum, r) => sum + Math.pow(r.npv, 2), 0) / results.length)).toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default MonteCarloSimulation;